import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Per-(user, workflow) canvas view preferences.
 *
 * Persists low-sensitivity UI choices (currently: layout orientation) keyed by
 * the composite `(tenantId, project, app, workflow)` identity used elsewhere
 * in the app (see `recentWorkStore`). Stored in `localStorage` via the same
 * try/catch-wrapped Zustand persist adapter pattern as `recentWorkStore`.
 *
 * Design notes:
 *   - Orientation is NOT stored in the workflow JSON document. It's a per-user
 *     viewing preference; collaborators may pick different orientations.
 *   - Tenant-namespaced keys prevent cross-tenant leakage when a user switches
 *     tenants but happens to have a workflow with the same project/app/name.
 *   - User-namespaced keys prevent cross-user leakage when two signed-in users
 *     share the same browser profile and tenant.
 *   - LRU-capped to avoid unbounded growth if a user touches many workflows.
 *   - Schema-validated on hydration; malformed entries are silently dropped.
 *   - No PII in the payload beyond identifiers already present in the URL.
 *   - The store interface is adapter-shaped: a future server-backed user-prefs
 *     API can replace the localStorage backend without touching callers.
 */

import type { LayoutOrientation } from './workflowStore';

const STORAGE_KEY = 'otto-workflow-view-prefs';
const STORAGE_VERSION = 1;
const MAX_ENTRIES = 500;

/**
 * Composite identity for a single workflow. Components match the URL path
 * structure: `/tenant/:tenantId/.../projects/:project/apps/:app/workflows/:workflow`,
 * plus a stable per-user identifier so two users sharing a browser profile and
 * tenant do not overwrite each other's preferences.
 */
export interface WorkflowIdentity {
  /**
   * Stable per-user identifier. Use MSAL `account.localAccountId` — it is the
   * canonical per-user id used elsewhere in the app (see `useAuth.ts` where
   * `localAccountId` is hashed for telemetry) and is stable across token
   * refreshes within a tenant. Fall back to `account.homeAccountId` only if
   * `localAccountId` is unavailable.
   */
  userId: string;
  tenantId: string;
  project: string;
  app: string;
  workflow: string;
}

interface WorkflowViewPref {
  orientation: LayoutOrientation;
  updatedAt: number;
}

interface WorkflowViewPrefsState {
  version: typeof STORAGE_VERSION;
  byWorkflow: Record<string, WorkflowViewPref>;
  /**
   * User-wide UI preferences (not per-workflow). `orientation` here is the
   * fallback when a workflow has no entry in `byWorkflow`; `canvasLocked`
   * has no per-workflow override and is purely global.
   */
  defaults: { orientation: LayoutOrientation; canvasLocked: boolean };
}

interface WorkflowViewPrefsActions {
  getOrientation: (identity: WorkflowIdentity) => LayoutOrientation | undefined;
  setOrientation: (identity: WorkflowIdentity, orientation: LayoutOrientation) => void;
  getDefault: () => LayoutOrientation;
  setDefault: (orientation: LayoutOrientation) => void;
  getCanvasLocked: () => boolean;
  setCanvasLocked: (locked: boolean) => void;
  removeByWorkflow: (identity: WorkflowIdentity) => void;
  clearByWorkflow: () => void;
  clearAll: () => void;
}

type WorkflowViewPrefsStore = WorkflowViewPrefsState & WorkflowViewPrefsActions;

/**
 * Compose a user- and tenant-namespaced storage key. All components are
 * URL-encoded to keep the `::` delimiter unambiguous (a workflow name could
 * otherwise contain "::"). `userId` is first so prefs are isolated per signed-in
 * user even when two users share the same browser profile and tenant.
 */
export function composeWorkflowKey(identity: WorkflowIdentity): string {
  return [identity.userId, identity.tenantId, identity.project, identity.app, identity.workflow]
    .map(encodeURIComponent)
    .join('::');
}

function isLayoutOrientation(value: unknown): value is LayoutOrientation {
  return value === 'horizontal' || value === 'vertical';
}

function isWorkflowViewPref(value: unknown): value is WorkflowViewPref {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return isLayoutOrientation(v.orientation) && typeof v.updatedAt === 'number';
}

/**
 * Validate a parsed payload on hydration and return only the well-formed
 * portion. Anything malformed is silently dropped — same failure mode as
 * `recentWorkStore`.
 */
function sanitizeState(raw: unknown): WorkflowViewPrefsState {
  const safe: WorkflowViewPrefsState = {
    version: STORAGE_VERSION,
    byWorkflow: {},
    defaults: { orientation: 'horizontal', canvasLocked: false },
  };

  if (typeof raw !== 'object' || raw === null) return safe;
  const obj = raw as Record<string, unknown>;

  if (obj.version !== STORAGE_VERSION) return safe;

  if (typeof obj.defaults === 'object' && obj.defaults !== null) {
    const d = obj.defaults as Record<string, unknown>;
    if (isLayoutOrientation(d.orientation)) safe.defaults.orientation = d.orientation;
    if (typeof d.canvasLocked === 'boolean') safe.defaults.canvasLocked = d.canvasLocked;
  }

  if (typeof obj.byWorkflow === 'object' && obj.byWorkflow !== null) {
    const entries = Object.entries(obj.byWorkflow as Record<string, unknown>)
      .filter(([k, v]) => typeof k === 'string' && k.length > 0 && isWorkflowViewPref(v)) as [string, WorkflowViewPref][];

    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => b[1].updatedAt - a[1].updatedAt);
      entries.length = MAX_ENTRIES;
    }

    for (const [k, v] of entries) {
      safe.byWorkflow[k] = { orientation: v.orientation, updatedAt: v.updatedAt };
    }
  }

  return safe;
}

/**
 * Enforce LRU cap after a write. Mutates the passed map and returns it.
 */
function evictIfOverCap(byWorkflow: Record<string, WorkflowViewPref>): Record<string, WorkflowViewPref> {
  const keys = Object.keys(byWorkflow);
  if (keys.length <= MAX_ENTRIES) return byWorkflow;

  const sorted = keys
    .map((k) => [k, byWorkflow[k]!.updatedAt] as const)
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_ENTRIES);

  const kept: Record<string, WorkflowViewPref> = {};
  for (const [k] of sorted) kept[k] = byWorkflow[k]!;
  return kept;
}

export const useWorkflowViewPrefsStore = create<WorkflowViewPrefsStore>()(
  persist(
    (set, get) => ({
      version: STORAGE_VERSION,
      byWorkflow: {},
      defaults: { orientation: 'horizontal', canvasLocked: false },

      getOrientation: (identity) => {
        const key = composeWorkflowKey(identity);
        return get().byWorkflow[key]?.orientation;
      },

      setOrientation: (identity, orientation) => {
        if (!isLayoutOrientation(orientation)) return;
        const key = composeWorkflowKey(identity);
        set((state) => ({
          byWorkflow: evictIfOverCap({
            ...state.byWorkflow,
            [key]: { orientation, updatedAt: Date.now() },
          }),
        }));
      },

      getDefault: () => get().defaults.orientation,

      setDefault: (orientation) => {
        if (!isLayoutOrientation(orientation)) return;
        set((state) => ({ defaults: { ...state.defaults, orientation } }));
      },

      getCanvasLocked: () => get().defaults.canvasLocked,

      setCanvasLocked: (locked) => {
        if (typeof locked !== 'boolean') return;
        set((state) => ({ defaults: { ...state.defaults, canvasLocked: locked } }));
      },

      removeByWorkflow: (identity) => {
        const key = composeWorkflowKey(identity);
        set((state) => {
          if (!(key in state.byWorkflow)) return state;
          const { [key]: _removed, ...rest } = state.byWorkflow;
          void _removed;
          return { byWorkflow: rest };
        });
      },

      clearByWorkflow: () => set({ byWorkflow: {} }),

      clearAll: () => set({ byWorkflow: {}, defaults: { orientation: 'horizontal', canvasLocked: false } }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        version: state.version,
        byWorkflow: state.byWorkflow,
        defaults: state.defaults,
      }),
      storage: {
        getItem: (name) => {
          try {
            const raw = localStorage.getItem(name);
            if (!raw) return null;
            const parsed: unknown = JSON.parse(raw);
            const stateLike = (parsed as { state?: unknown })?.state;
            return { state: sanitizeState(stateLike) } as { state: WorkflowViewPrefsState };
          } catch {
            try { localStorage.removeItem(name); } catch { /* storage unavailable */ }
            return null;
          }
        },
        setItem: (name, value) => {
          try { localStorage.setItem(name, JSON.stringify(value)); } catch { /* quota exceeded or private mode */ }
        },
        removeItem: (name) => {
          try { localStorage.removeItem(name); } catch { /* noop */ }
        },
      },
    },
  ),
);

export const __internalsForTesting = { sanitizeState, evictIfOverCap, MAX_ENTRIES, STORAGE_KEY, STORAGE_VERSION };
